import { useEffect } from 'react';

const BASE_URL  = process.env.REACT_APP_SITE_URL || 'https://www.jnf.org';
const OG_IMAGE  = `${BASE_URL}/og-banner.png`;
const SITE_NAME = 'JNF';
const BASE_TITLE = 'Jamuiyat Nasir Al-Fridaous Al Tijaniyyah';

/**
 * useSEO — Dynamically injects <title> and all meta tags per page.
 *
 * Usage:
 *   useSEO({
 *     title:       'Events',
 *     description: 'Upcoming Tijaniyyah events and programmes.',
 *     image:       'https://...optional-custom-image.jpg',
 *     url:         '/events',
 *     type:        'website',  // or 'article'
 *   });
 */
export default function useSEO({
  title,
  description = 'Jamuiyat Nasir Al-Fridaous Al Tijaniyyah — A Sufi Islamic JAMA following the Tijaniyyah Tariqa. Azkar, events, live radio, Sawia locations and member portal.',
  image,
  url = '/',
  type = 'website',
  article = null, // { publishedTime, modifiedTime, author }
} = {}) {
  useEffect(() => {
    const fullTitle  = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — ${BASE_TITLE}`;
    const fullURL    = `${BASE_URL}${url}`;
    const ogImage    = image || OG_IMAGE;

    // ── <title> ───────────────────────────────────────────────
    document.title = fullTitle;

    const setMeta = (selector, content) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const attr = selector.includes('property=') ? 'property' : 'name';
        const val  = selector.match(/["']([^"']+)["']/)?.[1];
        if (attr && val) el.setAttribute(attr, val);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Primary
    setMeta('meta[name="title"]',       fullTitle);
    setMeta('meta[name="description"]', description);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullURL);

    // Open Graph
    setMeta('meta[property="og:title"]',       fullTitle);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]',         fullURL);
    setMeta('meta[property="og:image"]',       ogImage);
    setMeta('meta[property="og:image:alt"]',   fullTitle);
    setMeta('meta[property="og:type"]',        type);
    setMeta('meta[property="og:site_name"]',   SITE_NAME);

    // Article-specific OG
    if (type === 'article' && article) {
      if (article.publishedTime) setMeta('meta[property="article:published_time"]', article.publishedTime);
      if (article.modifiedTime)  setMeta('meta[property="article:modified_time"]',  article.modifiedTime);
      if (article.author)        setMeta('meta[property="article:author"]',          article.author);
    }

    // Twitter / X
    setMeta('meta[name="twitter:title"]',       fullTitle);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:image"]',       ogImage);
    setMeta('meta[name="twitter:image:alt"]',   fullTitle);
    setMeta('meta[name="twitter:card"]',        'summary_large_image');
    setMeta('meta[name="twitter:url"]',         fullURL);

  }, [title, description, image, url, type]);
}

// ── Per-page SEO configs ──────────────────────────────────────
export const PAGE_SEO = {
  home: {
    title: undefined, // uses default full site title
    description: 'Welcome to JNF — Jamuiyat Nasir Al-Fridaous Al Tijaniyyah. Explore daily Azkar, Tijaniyyah Wird, upcoming events, gallery, Sawia locations and our live Islamic radio.',
    url: '/',
  },
  events: {
    title: 'Events',
    description: 'Upcoming Tijaniyyah events, programmes, and gatherings organised by JNF across all Sawia branches.',
    url: '/events',
  },
  azkar: {
    title: 'Azkar — Daily, Monthly & Special Wird',
    description: 'Access the complete Tijaniyyah Azkar — daily morning and evening remembrances, monthly Wird, Wazifah, Lazimiyyah and Hailalah with translations and audio.',
    url: '/azkar',
  },
  gallery: {
    title: 'Gallery',
    description: 'Browse the JNF gallery — photos, videos, audio recordings and PDF documents from our events and programmes.',
    url: '/gallery',
  },
  radio: {
    title: 'Live Radio — JNF Radio',
    description: 'Tune in to JNF Radio for live Islamic broadcasts, Qasidas, Tijaniyyah programmes and spiritual content.',
    url: '/radio',
  },
  sawia: {
    title: 'Sawia Locations',
    description: 'Find a Zawiya (Sawia) near you. Browse all JNF Sawia branches with addresses, contacts and Google Maps directions.',
    url: '/sawia',
  },
  quotes: {
    title: 'Murshid Quotes',
    description: 'Pearls of wisdom from our Murshid — inspirational quotes and teachings in Arabic with English translations.',
    url: '/quotes',
  },
  about: {
    title: 'About Us',
    description: 'Learn about Jamuiyat Nasir Al-Fridaous Al Tijaniyyah — our history, mission, and commitment to the Tijaniyyah Tariqa.',
    url: '/about',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with your local JNF Sawia branch. Find contact details, prayer times, and send us a message.',
    url: '/contact',
  },
  register: {
    title: 'Join JNF — Member Registration',
    description: 'Register as a member of Jamuiyat Nasir Al-Fridaous Al Tijaniyyah. Complete your profile and connect with the JAMA.',
    url: '/member/register',
  },
  directory: {
    title: 'Member Directory',
    description: 'Browse the JNF member directory — connect with fellow members of the Tijaniyyah JAMA across all locations.',
    url: '/member/dashboard/directory',
  },
};
